import * as React from 'react';
import { useContext } from 'react';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Settings, HelpCircle, Bug, LogOut, Moon, Sun, Globe } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useFlags } from '@flagsmith/flagsmith/react';

import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import PresenceDot from 'src/components/PresenceDot';
import { EPresence, isUserAway, type IUser } from 'src/api/users-api';
import { Auth } from 'src/services/api';
import { useSetPresence } from 'src/services/api/users/presence';
import { useAppState } from 'src/AppStateProvider';
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';
import { AuthenticatedStateContext } from 'src/authenticated-state/context';
import { getTimezoneAbbr } from 'src/utils/format-date-time';

import styles from 'src/components/layout/landlord/NavigationSidebarMenu.module.css';

type Props = {
    anchorEl: HTMLElement | null;
    setIsProfileMenuOpened: (isOpened: boolean) => void;
    isSidebarCollapsed: boolean;
    loggedInUser: IUser | undefined;
    openTeamSwitcher: () => void;
    onRequestSetAway: () => void;
}

export default function NavigationSidebarMenu({
    anchorEl,
    setIsProfileMenuOpened,
    loggedInUser,
    openTeamSwitcher,
    onRequestSetAway,
}: Props) {
    const { setHelpSidebarOpened } = useAppState();
    const navigate = useNavigate();
    const flags = useFlags([EFeatureFlag.TEAM_AVAILABILITY]);
    const isAvailabilityEnabled = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;
    const { mutate: setPresence } = useSetPresence();
    const isAway = isUserAway(loggedInUser);

    const authenticatedState = useContext(AuthenticatedStateContext);
    const timezoneOverride = authenticatedState?.userSettings?.timezone_override ?? null;
    const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const activeTimezone = timezoneOverride ?? systemTimezone;
    const timezoneAbbr = getTimezoneAbbr(activeTimezone);
    const timezoneReadable = activeTimezone.replace(/_/g, ' ');
    const timezoneDisplay = timezoneAbbr ?
        `${timezoneReadable} (${timezoneAbbr})` :
        timezoneReadable;

    const teamName = loggedInUser?.team?.name || '';
    const canSwitchTeam = Boolean(loggedInUser?.org_user_id);

    const close = () => setIsProfileMenuOpened(false);

    const handlePresenceClick = () => {
        if (isAway) {
            setPresence({ presence: EPresence.ONLINE });
            close();
        } else {
            close();
            onRequestSetAway();
        }
    };

    const handleLogout = () => {
        Auth.logout();
    };

    const handleClickReportABug = async () => {
        const feedback = Sentry.getFeedback();
        const form = await feedback?.createForm();
        form?.appendToDom();
        form?.open();
        close();
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open
            onClose={close}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            classes={{ paper: styles.menuPaper, list: styles.menuList }}
        >
            <div className={styles.header}>
                <div className={styles.avatarWrap}>
                    <ProfileImageRounded
                        url={loggedInUser?.profile_photo_url ?? null}
                        userName={loggedInUser?.name ?? ''}
                        width={44}
                        height={44}
                    />
                    {isAvailabilityEnabled && (
                        <span className={`${styles.avatarRing} ${isAway ? styles.avatarRingAway : styles.avatarRingOnline}`} />
                    )}
                </div>

                <div className={styles.headerText}>
                    <Typography variant="body2" className={styles.userName}>
                        {loggedInUser?.name}
                    </Typography>
                    {isAvailabilityEnabled && (
                        <div className={styles.userStatus}>
                            <PresenceDot presence={isAway ? EPresence.AWAY : EPresence.ONLINE} size={6} />
                            <Typography variant="caption" className={isAway ? styles.statusAway : styles.statusOnline}>
                                {isAway ? 'Away' : 'Active'}
                            </Typography>
                        </div>
                    )}
                    {teamName && (
                        <Typography variant="caption" className={styles.teamName}>{teamName}</Typography>
                    )}
                    <Typography variant="caption" component="span" className={styles.timezoneLabel}>
                        <Globe size={10} className={styles.timezoneGlobeIcon} />
                        {timezoneDisplay}
                    </Typography>
                </div>

                {canSwitchTeam && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { openTeamSwitcher(); close(); }}
                        className={styles.switchBtn}
                    >
                        Switch
                    </Button>
                )}
            </div>

            <Divider className={styles.divider} />

            {isAvailabilityEnabled && (
                <div className={styles.presenceSection}>
                    <button
                        className={`${styles.presenceBtn} ${isAway ? styles.presenceBtnAway : styles.presenceBtnOnline}`}
                        onClick={handlePresenceClick}
                        data-testid="toggle-presence"
                    >
                        <span className={styles.presenceBtnIcon}>
                            {isAway ? <Sun size={15} /> : <Moon size={15} />}
                        </span>
                        <span className={styles.presenceBtnBody}>
                            <span className={styles.presenceBtnLabel}>
                                {isAway ? 'Set yourself active' : 'Set yourself away'}
                            </span>
                            <span className={styles.presenceBtnSub}>
                                {isAway ? 'Resume receiving leads' : 'Teammates can cover your leads'}
                            </span>
                        </span>
                    </button>
                </div>
            )}

            <Divider className={styles.divider} />

            <div className={styles.navSection}>
                <MenuItem
                    onClick={() => { navigate('/landlord/inner/settings'); close(); }}
                    className={styles.menuItem}
                >
                    <Settings size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>Settings</Typography>
                </MenuItem>
                <MenuItem
                    onClick={() => { setHelpSidebarOpened(true); close(); }}
                    className={styles.menuItem}
                >
                    <HelpCircle size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>Help</Typography>
                </MenuItem>
                <MenuItem onClick={handleClickReportABug} className={styles.menuItem}>
                    <Bug size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>Report a Bug</Typography>
                </MenuItem>
            </div>

            <Divider className={styles.divider} />

            <div className={styles.logoutSection}>
                <MenuItem onClick={handleLogout} className={`${styles.menuItem} ${styles.menuItemDestructive}`}>
                    <LogOut size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabelDestructive}>Log out</Typography>
                </MenuItem>
            </div>
        </Menu>
    );
}
